import { combineData } from '@amazon/vinyl-observable'
import type { Unsubscribe } from '@amazon/vinyl-util'
import {
    isAdvertisementEntry,
    isPodcastEntry,
    type PlaylistController,
    type RepeatMode,
} from '@amzn/amp-playback'
import type { CloudQueueModeWriter } from '@/cloudQueue/cloudQueueModeWriter'
import { ctx } from '@/util/context'

const { errorHandler } = ctx('podcastRepeatGuard')

export interface PodcastRepeatGuardDeps {
    readonly playlistController: Pick<PlaylistController, 'current' | 'repeatMode'>
    readonly modeWriter: Pick<CloudQueueModeWriter, 'setRepeatMode'>
}

/**
 * Podcasts never play in repeat (GH): while an episode is current, repeat is held
 * off, and the mode the user had set comes back with the next music entry.
 */
export function bindPodcastRepeatOff(deps: PodcastRepeatGuardDeps): Unsubscribe {
    const { playlistController, modeWriter } = deps
    let musicRepeat: RepeatMode | null = null
    let queued = false
    let stopped = false

    const write = (mode: RepeatMode): void => {
        modeWriter.setRepeatMode(mode).catch(errorHandler)
    }
    const reconcile = () => {
        queued = false
        if (stopped) return
        const current = playlistController.current.value
        const mode = playlistController.repeatMode.value
        if (isPodcastEntry(current)) {
            if (mode === 'off') return
            musicRepeat = mode
            write('off')
        } else if (musicRepeat && current && !isAdvertisementEntry(current)) {
            const restored = musicRepeat
            musicRepeat = null
            write(restored)
        }
    }

    const unsubscribe = combineData({
        current: playlistController.current,
        mode: playlistController.repeatMode,
    }).onData(() => {
        // The writer sets the mode synchronously, which a data callback may not do.
        if (queued) return
        queued = true
        queueMicrotask(reconcile)
    })
    return () => {
        stopped = true
        unsubscribe()
    }
}
